import { z } from 'zod'
import { LINK_SOURCES } from '../enums'
import { optionalId, optionalText, requiredText } from './common'
import type { TabuExtract } from './tabu'

/**
 * Multipart fields that accompany the uploaded file. At least one association is
 * required; the case↔client pairing is verified server-side (spec §5.1).
 */
export const documentUploadSchema = z
  .object({
    name: optionalText(300),
    description: optionalText(2000),
    caseId: optionalId(),
    clientId: optionalId(),
    taskId: optionalId(),
    documentTypeId: optionalId(),
  })
  .superRefine((v, ctx) => {
    if (!v.caseId && !v.clientId && !v.taskId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['caseId'],
        message: 'חובה לשייך את המסמך לתיק, ללקוח או למשימה',
      })
    }
  })

export type DocumentUploadInput = z.infer<typeof documentUploadSchema>

export const documentUpdateSchema = z.object({
  name: requiredText(300, 'שם המסמך הוא שדה חובה'),
  description: optionalText(2000),
})

export type DocumentUpdateInput = z.infer<typeof documentUpdateSchema>

export const documentQuerySchema = z.object({
  caseId: optionalId(),
  clientId: optionalId(),
  taskId: optionalId(),
  search: optionalText(200),
})

export type DocumentQuery = z.infer<typeof documentQuerySchema>

export type DocumentDto = {
  id: string
  name: string
  description: string | null
  driveFileId: string
  mimeType: string | null
  sizeBytes: number | null
  caseId: string | null
  caseName: string | null
  clientId: string | null
  clientName: string | null
  taskId: string | null
  taskName: string | null
  documentTypeId: string | null
  documentTypeName: string | null
  uploadedById: string
  uploadedByName: string | null
  uploadedAt: string
  /** Present only after the user ran "חילוץ נסח" on this document. */
  tabuExtract: TabuExtract | null
  /** Why this document appears in the list the caller asked for. */
  source: (typeof LINK_SOURCES)[number]
}

/** The case documents view: case-wide documents plus a group per client in the case. */
export type CaseDocumentsDto = {
  caseDocuments: DocumentDto[]
  byClient: {
    clientId: string
    clientName: string
    documents: DocumentDto[]
  }[]
}

export const documentTypeInputSchema = z.object({
  name: requiredText(100, 'שם סוג המסמך הוא שדה חובה'),
})

export type DocumentTypeInput = z.infer<typeof documentTypeInputSchema>

export type DocumentTypeDto = {
  id: string
  name: string
}
